import CurrencyDropdown from '@/components/CommonComponents/CurrencyDropdown';
import CustomDropdown from '@/components/CommonComponents/CustomDropdown';
import InlineInputField from '@/components/CommonComponents/InlineInputField';
import MultiSelectDropdown from '@/components/CommonComponents/MultiSelectDropdown';
import { useDebounce } from '@/hooks/useDebounce';
import { AppDispatch, RootState } from '@/store';
import { fetchMetaData } from '@/store/slices/metaDataSlice/metaDataSlice';
import { Language } from '@/types/metaData';
import { GeoFenceInputDto, SiteFormData } from '@/types/project.types';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { PlusIcon } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  Circle,
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';
import tzlookup from 'tz-lookup';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface ProjectSiteLocationsProps {
  sites: SiteFormData[];
  handleSiteChange: (
    index: number,
    field: keyof SiteFormData,
    value: string | number | number[] | GeoFenceInputDto | Language[],
  ) => void;
  addSite: () => void;
}

const MapUpdater: React.FC<{ center: [number, number]; zoom: number }> = ({ center, zoom }) => {
  const map = useMap();

  useEffect(() => {
    if (map) {
      setTimeout(() => {
        map.invalidateSize();
        map.setView(center, zoom);
      }, 100);
    }
  }, [center, zoom, map]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && map) {
        setTimeout(() => map.invalidateSize(), 100);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [map]);

  return null;
};

const LocationPicker: React.FC<{
  onLocationSelect: (lat: number, lng: number) => void;
}> = ({ onLocationSelect }) => {
  const handleMapClick = useCallback(
    (e: any) => {
      const { lat, lng } = e.latlng;
      onLocationSelect(lat, lng);
    },
    [onLocationSelect],
  );

  useMapEvents({
    click: handleMapClick,
  });

  return null;
};

const ProjectSiteLocations: React.FC<ProjectSiteLocationsProps> = ({
  sites,
  handleSiteChange,
  addSite,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const { countries, languages, loading } = useSelector((state: RootState) => state.metaData);

  useEffect(() => {
    dispatch(fetchMetaData());
  }, [dispatch]);

  return (
    <div className="mx-auto text-[#621C43]">
      {/* Header */}
      <div className="border-b border-b-[#621C43] px-5 py-6 flex flex-col">
        <h2 className="text-xl font-bold">Project Site Locations</h2>
        <p className="text-base font-medium">Define the geolocation of sites.</p>
      </div>

      {/* Grid Layout */}
      <div
        className={`grid gap-6 p-6 ${
          sites.length > 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-3'
        }`}
      >
        {(sites || []).map((site, index) => (
          <SiteInputCard
            key={index}
            site={site}
            index={index}
            countries={countries}
            languages={languages}
            loading={loading}
            handleSiteChange={handleSiteChange}
          />
        ))}

        {/* Add Site Button */}
        <button
          type="button"
          onClick={addSite}
          className="flex justify-center items-center content-center bg-[#44042a62] rounded-md cursor-pointer"
        >
          <div className="flex flex-col self-center items-center p-4 rounded-md">
            <PlusIcon size={45} className="text-[#621C43]" />
            <span className="italic text-xl text-[#621C43] mt-2">Add more sites</span>
          </div>
        </button>
      </div>
    </div>
  );
};

const SiteInputCard: React.FC<{
  site: SiteFormData;
  index: number;
  countries: any[];
  languages: any[];
  loading: boolean;
  handleSiteChange: (
    index: number,
    field: keyof SiteFormData,
    value: string | number | number[] | GeoFenceInputDto | Language[],
  ) => void;
}> = ({ site, index, countries, languages, loading, handleSiteChange }) => {
  const debouncedLat = useDebounce(site.geofence.centerLat || null, 400);
  const debouncedLng = useDebounce(site.geofence.centerLng || null, 400);

  // Get current location on component mount for all sites

  const hasSetInitialLocation = useRef(false);

  useEffect(() => {
    if (site.name || hasSetInitialLocation.current) return;

    if (site.geofence.centerLat === null && site.geofence.centerLng === null) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            let timeZone = null;

            try {
              timeZone = tzlookup(lat, lng);
            } catch (error) {
              timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            }
            handleSiteChange(index, 'geofence', {
              ...site.geofence,
              centerLat: lat,
              centerLng: lng,
              timezone: timeZone,
            });
          },
          (error) => {},
        );
      }
    }
  }, []);

  const mapCenter = useMemo<[number, number]>(() => {
    const lat = debouncedLat || 20.5937;
    const lng = debouncedLng || 78.9629;

    if (lat === 0 && lng === 0) {
      return [20.5937, 78.9629];
    }

    return [lat, lng];
  }, [debouncedLat, debouncedLng]);

  const hasValidCoordinates = useMemo(() => {
    return (
      debouncedLat !== null && debouncedLng !== null && debouncedLat !== 0 && debouncedLng !== 0
    );
  }, [debouncedLat, debouncedLng]);

  const mapZoom = useMemo(() => {
    if (!site.geofence.radiusMeters) {
      return 4;
    }
    if (site.geofence.radiusMeters > 0) {
      if (site.geofence.radiusMeters > 50000) return 6;
      if (site.geofence.radiusMeters > 10000) return 10;
      if (site.geofence.radiusMeters > 5000) return 12;
      return 14;
    }
    return hasValidCoordinates ? 10 : 4;
  }, [site.geofence.radiusMeters, hasValidCoordinates]);

  const handleLocationSelect = useCallback(
    (lat: number, lng: number) => {
      let timeZone = null;

      try {
        timeZone = tzlookup(lat, lng);
      } catch (error) {
        timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      }

      handleSiteChange(index, 'geofence', {
        ...site.geofence,
        centerLat: lat,
        centerLng: lng,
        timezone: timeZone,
      });
    },
    [handleSiteChange, index, site.geofence],
  );

  return (
    <div className="rounded-lg">
      <h2 className="font-semibold mb-2">
        Add Site {index + 1} {index === 0 ? '(required)' : index === 1 ? '(optional)' : ''}
      </h2>

      <div className="grid grid-cols-1 gap-2">
        {/* Name */}
        <div className="relative">
          <InlineInputField
            value={site.name}
            onChange={(e) => handleSiteChange(index, 'name', e.target.value)}
            placeholder="Site Name"
            // required={true}
            starDistance={0}
          />
          {site.name.trim() === '' && (
            <span className="absolute left-[calc(0.5rem+8.3ch)] top-2 text-red-500 pointer-events-none">
              *
            </span>
          )}
          {site.name.trim() !== '' &&
            (site.name.trim().length < 5 || site.name.trim().length > 60) && (
              <div className="text-red-500 text-xs mt-1">Site name must be 5–60 characters.</div>
            )}
        </div>

        {/* Country */}
        <div className="relative">
          <CustomDropdown
            value={site.countryId}
            onChange={(value) => handleSiteChange(index, 'countryId', Number(value))}
            options={countries.map((country) => ({
              value: country.id,
              label: country.name,
            }))}
            placeholder="Country"
            required
            requiredIndicatorPosition="left-[calc(0.5rem+7ch)]"
            className="w-full rounded-lg focus:outline-none text-[#621C43] max-h-[200px] bg-[#4A082D33]/10 appearance-none"
          />
          {!site.countryId && (
            <span className="absolute left-[calc(0.5rem+7ch)] top-2 text-red-500 pointer-events-none">
              *
            </span>
          )}
        </div>

        {/* Coordinates */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <InlineInputField
              type="number"
              value={site.geofence.centerLat === null ? '' : site.geofence.centerLat}
              onChange={(e) => {
                const lat = e.target.value === '' ? null : Number(e.target.value);
                let timeZone = null;
                if (lat && site.geofence.centerLng) {
                  try {
                    timeZone = tzlookup(lat, site.geofence.centerLng);
                  } catch (error) {}
                }
                handleSiteChange(index, 'geofence', {
                  ...site.geofence,
                  centerLat: lat,
                  timezone: timeZone || site.geofence.timezone,
                });
              }}
              placeholder="Latitude"
            />
            {site.geofence.centerLat === null && (
              <span className="absolute left-[calc(0.5rem+7ch)] top-2 text-red-500 pointer-events-none">
                *
              </span>
            )}
          </div>
          <div className="relative flex-1">
            <InlineInputField
              type="number"
              value={site.geofence.centerLng === null ? '' : site.geofence.centerLng}
              onChange={(e) => {
                const lng = e.target.value === '' ? null : Number(e.target.value);
                let timeZone = null;
                if (lng && site.geofence.centerLat) {
                  try {
                    timeZone = tzlookup(site.geofence.centerLat, lng);
                  } catch (error) {}
                }
                handleSiteChange(index, 'geofence', {
                  ...site.geofence,
                  centerLng: lng,
                  timezone: timeZone || site.geofence.timezone,
                });
              }}
              placeholder="Longitude"
            />
            {site.geofence.centerLng === null && (
              <span className="absolute left-[calc(0.5rem+8ch)] top-2 text-red-500 pointer-events-none">
                *
              </span>
            )}
          </div>
        </div>

        {/* Formatted Coordinates Display */}
        {hasValidCoordinates && (
          <div className="text-sm text-[#621C43] bg-[#4A082D33] p-2 rounded-lg text-center">
            Longitude - {Math.abs(debouncedLng!).toFixed(4)}° {debouncedLng! >= 0 ? 'E' : 'W'} and
            Latitude - {Math.abs(debouncedLat!).toFixed(4)}° {debouncedLat! >= 0 ? 'N' : 'S'}
          </div>
        )}

        {/* Radius & Users */}
        <div className="flex gap-2 relative">
          <InlineInputField
            type="number"
            value={site.geofence.radiusMeters === null ? '' : site.geofence.radiusMeters}
            onChange={(e) => {
              const radius = e.target.value === '' ? null : Math.max(1, Number(e.target.value));

              handleSiteChange(index, 'geofence', {
                ...site.geofence,
                radiusMeters: radius,
              });
            }}
            placeholder="Radius (in meters)"
          />
          {site.geofence.radiusMeters === null && (
            <span className="absolute left-[calc(0.5rem+15ch)] top-2 text-red-500 pointer-events-none">
              *
            </span>
          )}
          {/* <InlineInputField
            type="number"
            value={site.maxUsers === 0 ? '' : site.maxUsers}
            onChange={(e) =>
              handleSiteChange(
                index,
                'maxUsers',
                e.target.value === '' ? 0 : Math.max(0, parseInt(e.target.value) || 0),
              )
            }
            placeholder="Max Users"
          /> */}
        </div>

        {/* Language */}
        <div className="relative">
          <MultiSelectDropdown
            options={languages || []}
            selectedIds={site.languages}
            onChange={(selectedIds) => handleSiteChange(index, 'languages', selectedIds)}
            placeholder="Choose Language(s)"
            disabled={loading}
            // className="z-[999]"
          />
          {site.languages.length === 0 && (
            <span className="absolute left-[calc(0.5rem+15.5ch)] top-2 text-red-500 pointer-events-none z-10">
              *
            </span>
          )}
        </div>

        {/* Currency */}
        <CurrencyDropdown
          value={site.currency}
          onChange={(value) => handleSiteChange(index, 'currency', value)}
          placeholder="Choose Reward Currency"
          required={true}
          requiredIndicatorPosition="left-[calc(0.5rem+20ch)]"
        />

        {/* Interactive Map */}
        <div className="bg-gray-200 rounded-xl overflow-hidden h-64 relative">
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            scrollWheelZoom={true}
            className="h-full w-full z-0 cursor-crosshair"
            key={`map-${index}`}
            whenReady={() => {
              setTimeout(() => {}, 100);
            }}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

            <MapUpdater center={mapCenter} zoom={mapZoom} />
            <LocationPicker onLocationSelect={handleLocationSelect} />

            {hasValidCoordinates && (
              <>
                {site.geofence.radiusMeters && (
                  <Circle
                    center={mapCenter}
                    radius={site.geofence.radiusMeters}
                    pathOptions={{
                      fillColor: '#621C43',
                      fillOpacity: 0.2,
                      color: '#621C43',
                      weight: 2,
                    }}
                  />
                )}

                {hasValidCoordinates &&
                  site.geofence.centerLat !== null &&
                  site.geofence.centerLng !== null && (
                    <Marker
                      position={[site.geofence.centerLat, site.geofence.centerLng]}
                      draggable={true}
                      eventHandlers={{
                        dragend: (e) => {
                          const position = e.target.getLatLng();
                          handleLocationSelect(position.lat, position.lng);
                        },
                      }}
                    >
                      <Popup>
                        <strong>{site.name || `Site ${index + 1}`}</strong>
                        <br />
                        Lat: {debouncedLat?.toFixed(4)}, Lng: {debouncedLng?.toFixed(4)}
                        {(site.geofence.radiusMeters || 1000) > 0 && (
                          <>
                            <br />
                            Radius: {site.geofence.radiusMeters}m
                          </>
                        )}
                        <br />
                        <em>Drag marker to move location</em>
                      </Popup>
                    </Marker>
                  )}
              </>
            )}
          </MapContainer>
          {!hasValidCoordinates && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-80 pointer-events-none">
              <p className="text-gray-500 text-sm">Click on map to select location</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectSiteLocations;
